import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MonitoringAlarm } from '../../models/monitoring-alarm';
import { TranslateService } from '@ngx-translate/core';
import { Message } from 'primeng/api/message';
import { environment } from '../../../../../environments/environment';
import { ProjectConfiguration } from '../../../project/models/project-configuration';
import { RaiseIncidentFormComponent } from '../../../incidents/components/raise-incident-form/raise-incident-form.component';
import { MonitoredTrain } from '../../../monitoring/models/monitored-train';
import { Incident } from '../../../incidents/models/incident';
import { IncidentDataService } from '../../../incidents/services/incident-data.service';
import { MessageService, SelectItem } from 'primeng/api';
import { Subscription } from 'rxjs';
import { UserProfileService } from '../../../../features/users/services/user-profile.service';
import { TableStateService } from '../../../../shared/services/table-state.service';

@Component({
    selector: 'app-monitored-train-alarms',
    template: require('./monitored-train-alarms.component.html'),
    styles: [require('./monitored-train-alarms.component.sass')]
})
export class MonitoredTrainAlarmsComponent implements OnInit, OnChanges, OnDestroy {

    @ViewChild(RaiseIncidentFormComponent) raiseIncidentForm: RaiseIncidentFormComponent;
    @Input() projectConfiguration: ProjectConfiguration;
    @Input() alarms: MonitoringAlarm[];
    @Input() monitoredTrains: MonitoredTrain[];
    @Input() projectId: number;
    @Input() isSingleTrain: boolean;
    @Input() selectedAlarmList: MonitoringAlarm[] = [];
    @Input() alarmStateKey: string;
    @Output() investigateTrain = new EventEmitter<{
        projectId: number,
        trainId: number
    }>();

    @Output() acknowledgeAlarm = new EventEmitter<MonitoringAlarm[]>();
    @Output() investigateAlarm = new EventEmitter<MonitoringAlarm[]>();
    @Output() raiseIncident = new EventEmitter<Incident>();
    @Output() selectedAlarmListChange = new EventEmitter<MonitoringAlarm[]>();

    alarmDateFormat = environment.alarms.dateFormat;
    paintedCoachEnabled: boolean;
    isRFEnabled: boolean;
    incidentURL: string;
    currentTrain: MonitoredTrain | undefined;


    filteredAlarms: MonitoringAlarm[];
    tableFilters: any = {};

    languageChangeSubscription: Subscription;

    columns: any[];

    tableConfiguration: any = {
        paginate: true,
        sortableMode: 'multiple',
        defaultPagination: 250,
        itemsPerPageOptions: [100, 250, 500],
        exportFilename: 'train-alarms'
    };

    selectAlarmCategoryId = 1;


    messages: Message[] = [{
        severity: 'info',
        summary: 'Info',
        detail: this.translate.instant('I18N_MONITORING.NO_ALARMS')
    }];

    isCustomer = false;

    acknowledgedOptions: SelectItem[];
    selectedAcknowledgedOption: boolean | null;
    severityOptions: SelectItem[];
    selectedSeverityOption: number | null;

    @ViewChild('dt') dt!: any;

    constructor(
        private translate: TranslateService,
        private incidentDataService: IncidentDataService,
        private messageService: MessageService,
        private userProfile: UserProfileService,
        public tableState: TableStateService
    ) {
        this.languageChangeSubscription = this.translate.onLangChange.subscribe(() => {
            this.refreshAlarmTable();
        });
    }

    ngOnInit(): void {
        const userData = this.userProfile.getUserData();
        this.isCustomer = this.userProfile.isAdmin() ? false : (userData?.projectRoleSet ? userData?.projectRoleSet[this.projectId]?.toLocaleLowerCase() === "customer" : false)
    }


    ngOnChanges(): void {
        if (this.isSingleTrain) {
            this.selectAlarmCategoryId = 0;
        }
        this.paintedCoachEnabled = this.projectConfiguration && this.projectConfiguration.paintedCoach;
        const remedyForceConfiguration = this.projectConfiguration && this.projectConfiguration.remedyForceConfiguration;
        this.isRFEnabled = remedyForceConfiguration && remedyForceConfiguration.rfEnabled;
        this.incidentURL = remedyForceConfiguration && remedyForceConfiguration.incidentUrl;
        this.refreshAlarmTable();
    }

    ngAfterViewInit() {
        if (!this.isSingleTrain) {
            this.selectedAcknowledgedOption = false;
            this.selectedSeverityOption = null;
            this.dt.filter(this.selectedAcknowledgedOption, 'acknowledged', 'equals')
            this.dt.filter(this.selectedSeverityOption, 'severity', 'equals')
        }
    }

    refreshAlarmTable = () => {
        this.columns = [
            {
                header: this.translate.instant('I18N_MONITORING.PROJECT'),
                field: 'projectName',
                type: 'text'
            }, {
                header: this.translate.instant('I18N_MONITORING.TRAIN'),
                field: 'trainName',
                type: 'text'
            }, {
                header: this.translate.instant('I18N_MONITORING.SEVERITY'),
                field: 'severity',
                type: 'severity',
                width: '200px'
            }, {
                header: this.translate.instant('I18N_MONITORING.DEVICE'),
                field: 'deviceName',
                type: 'text'
            }, {
                header: this.translate.instant('I18N_MONITORING.DESCRIPTION'),
                field: 'description',
                type: 'text',
                width: '300px'
            }, {
                header: this.translate.instant('I18N_MONITORING.PAINTED_CAR_NUMBER'),
                field: 'paintedCoachId',
                type: 'text'
            }, {
                header: this.translate.instant('I18N_MONITORING.DATE'),
                field: 'lastChange'
            }, {
                header: this.translate.instant('I18N_MONITORING.ACKNOWLEDGED'),
                field: 'acknowledged',
                type: 'acknowledged'
            }, {
                header: this.translate.instant('I18N_MONITORING.INCIDENT'),
                field: 'incidentId',
                type: 'text'
            }, {
                header: this.translate.instant('I18N_MONITORING.STATUS'),
                field: 'incidentStatus',
                type: 'text'
            }];
        this.columns = this.columns.map(column => {
            column.visible = true;
            if (column.field === 'projectName') {
                column.visible = !(this.projectId > 0);
            }
            if (column.field === 'paintedCoachId') {
                column.visible = this.paintedCoachEnabled;
            }
            if (column.field === 'incidentId' || column.field === 'incidentStatus') {
                column.visible = this.isRFEnabled;
            }
            return column;
        });
        this.acknowledgedOptions = [{
            label: this.translate.instant('I18N_GENERAL.ALL'),
            value: null
        }, {
            label: this.translate.instant('I18N_GENERAL.YES'),
            value: true
        },
        {
            label: this.translate.instant('I18N_GENERAL.NO'),
            value: false
        }];

        this.severityOptions = [
            {
                label: this.translate.instant('I18N_MONITORING.LOW'),
                value: 2
            },
            {
                label: this.translate.instant('I18N_MONITORING.MEDIUM'),
                value: 3
            },
            {
                label: this.translate.instant('I18N_MONITORING.HIGH'),
                value: 4
            },
            {
                label: this.translate.instant('I18N_MONITORING.FATAL'),
                value: 5
            }
        ];
    }

    clearSelectedAlarms = () => {
        this.selectedAlarmList = [];
    }

    onRaiseRFIncident = () => {
        this.currentTrain = this.monitoredTrains && this.monitoredTrains.find(train => train.trainId === this.selectedAlarmList[0].trainId);
        this.raiseIncidentForm.showModal(this.currentTrain, this.selectedAlarmList);
    }

    onAcknowledgeAllAlarms = () => {
        this.selectAlarmCategoryId = 0;
        this.acknowledgeAlarm.emit(this.alarms.filter(alarm => !alarm.acknowledged));
        this.selectedAlarmListChange.emit(this.selectedAlarmList);
    }

    onAcknowledgeAlarms = () => {
        this.selectAlarmCategoryId = 0;
        this.acknowledgeAlarm.emit(this.selectedAlarmList.filter(alarm => !alarm.acknowledged));
        this.selectedAlarmListChange.emit(this.selectedAlarmList);
    }

    onInvestigateZabbix = () => {
        this.investigateAlarm.emit(this.selectedAlarmList.filter(alarm => alarm.type === 'zabbix-alarm'));
    }

    onInvestigateTrain = (alarm: MonitoringAlarm) => {
        this.investigateTrain.emit({
            trainId: alarm.trainId,
            projectId: alarm.projectId
        });
    }
    onIncidentIdClick = (incidentPk: string) => {
        if (this.incidentURL) {
            window.open(this.incidentURL.replace('ID', incidentPk));
        } else {
            alert('RemedyForce URL is not found in Project Configuration');
        }
    }


    handleFilter(data: {
        filters: any,
        filteredValue: MonitoringAlarm[]
    }) {
        this.tableFilters = data.filters;
        if (Object.keys(this.tableFilters).length > 0) {
            this.filteredAlarms = data.filteredValue;
        } else {
            this.filteredAlarms = [];
        }

    }

    onSubmitIncident = async (data: { parentType: string, incident: Incident }) => {
        if (data.incident) {
            try {
                const result = await this.incidentDataService.create(data.parentType, data.incident);
                if (!result.errors) {
                    const incidentId = result.incidentWithIDs && Object.keys(result.incidentWithIDs)[0];
                    this.selectedAlarmList = this.selectedAlarmList.map(alarm => {
                        alarm.incidentId = incidentId;
                        return alarm;
                    });
                    this.onAcknowledgeAlarms();
                    this.messageService.add({
                        severity: 'success',
                        summary: this.translate.instant('I18N_RAISEINCIDENT.RAISE_INCIDENT'),
                        detail: this.translate.instant('I18N_RAISEINCIDENT.SUCCESSFULLY_SUBIMT_INCIDENT')
                    });
                } else {
                    this.messageService.add({
                        severity: 'error',
                        summary: this.translate.instant('I18N_RAISEINCIDENT.RAISE_INCIDENT'),
                        detail: result.errors
                    });
                }
            } catch (error) {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('I18N_RAISEINCIDENT.RAISE_INCIDENT'),
                    detail: this.translate.instant('I18N_RAISEINCIDENT.FAILED_TO_SUBMIT_INCIDENT')
                });
            } finally {
                this.raiseIncident.emit();
            }
        }
    }

    ngOnDestroy() {
        this.languageChangeSubscription.unsubscribe();
    }
}
